import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import HeaderLogged from './components/header_logged';
import COGSToolbar from './components/COGSToolbar';
import COGSTable from './components/COGSTable';
import BusinessExpensesPanel from './components/BusinessExpensesPanel';
import api from './api/client';
import './ProductCOGSPage.css';

// ── Tab definitions ──────────────────────────────────────────────────────────

const TABS = [
  { slug: 'sets',                    label: 'Sets' },
  { slug: 'wildflowers-microgreens', label: 'Wildflowers & Microgreens' },
  { slug: 'small-bags',              label: 'Small Bags' },
  { slug: 'fertilizers',             label: 'Fertilizers' },
  { slug: 'ready-products',          label: 'Ready Products' },
  { slug: 'kits',                    label: 'Kits' },
];

const COLUMNS = {
  sets: [
    { key: 'product_name',    label: 'Product Name',    isName: true },
    { key: 'sku',             label: 'SKU' },
    { key: 'asin',            label: 'ASIN' },
    { key: 'master_bag_cost', label: 'Master Bag Cost', isCost: true, editType: 'master-bag', editKey: 'sku' },
    { key: 'small_bags_cost', label: 'Small Bags Cost', isCost: true, hasBreakdown: 'small_bags_breakdown' },
    { key: 'tools',           label: 'Tools',           isCost: true, isFixedTools: true },
    { key: 'desiccant',       label: 'Desiccant',       isCost: true },
    { key: 'labor_hours',     label: 'Labor',           isCost: true, isLabor: true, isLaborShiftBased: true },
    { key: 'lost',            label: 'Lost',            isLost: true },
    { key: 'cogs',            label: 'COGS',            isCalculated: true },
  ],
  'wildflowers-microgreens': [
    { key: 'product_name',         label: 'Product Name', isName: true },
    { key: 'sku',                  label: 'SKU' },
    { key: 'asin',                 label: 'ASIN' },
    { key: 'master_bags',          label: 'Master Bags',          isCost: true, editType: 'master-bag', editKey: 'sku' },
    { key: 'seeds',                label: 'Seeds',                isCost: true, isSeedsCost: true },
    { key: 'seeds_other_expenses', label: 'Other order expenses', isCost: true, isSeedsOtherExp: true,
      tooltip: 'Packaging + Freight & Handling + Miscellaneous from the seeds order, allocated per lb' },
    { key: 'labor_hours',          label: 'Labor',                isCost: true, isLabor: true, isLaborShiftBased: true, estimatedFlag: 'labor_hours_estimated' },
    { key: 'lost',                 label: 'Lost',                 isLost: true },
    { key: 'cogs',                 label: 'COGS',                 isCalculated: true },
  ],
  'small-bags': [
    { key: 'product_name',         label: 'Small Bag', isName: true },
    { key: 'sku',                  label: 'SKU' },
    { key: 'asin',                 label: 'ASIN' },
    { key: 'bag_cost',             label: 'Bag Cost',             isCost: true, estimatedFlag: 'bag_cost_estimated', editType: 'small-bag', editKey: 'sku' },
    { key: 'seeds',                label: 'Seeds',                isCost: true, isSeedsCost: true },
    { key: 'seeds_other_expenses', label: 'Other order expenses', isCost: true, isSeedsOtherExp: true,
      tooltip: 'Packaging + Freight & Handling + Miscellaneous from the seeds order, allocated per lb' },
    { key: 'labor_hours',          label: 'Labor',                isCost: true, isLabor: true, estimatedFlag: 'labor_hours_estimated' },
    { key: 'lost',                 label: 'Lost',                 isLost: true },
    { key: 'cogs',                 label: 'COGS',                 isCalculated: true },
  ],
  fertilizers: [
    { key: 'product_name',        label: 'Product',    isNameSimple: true },
    { key: 'sku',                 label: 'SKU' },
    { key: 'asin',                label: 'ASIN' },
    { key: 'master_bag_cost',     label: 'Master Bag', isCost: true, editType: 'master-bag', editKey: 'sku' },
    { key: 'fert_cost',           label: 'Fertilizer', isCost: true, isFertCost: true },
    { key: 'fert_other_expenses', label: 'Other order expenses', isCost: true, isFertOtherExp: true,
      tooltip: 'Packaging + Freight & Handling + Miscellaneous from the fertilizer order, allocated per lb' },
    { key: 'labor_hours',         label: 'Labor',      isCost: true, isLabor: true, isLaborShiftBased: true },
    { key: 'lost',                label: 'Lost',       isLost: true },
    { key: 'cogs',                label: 'COGS',       isCalculated: true },
  ],
  'ready-products': [
    { key: 'product_name',   label: 'Product',             isNameSimple: true },
    { key: 'sku',            label: 'SKU' },
    { key: 'asin',           label: 'ASIN' },
    { key: 'product_cost',   label: 'Product Cost',        isCost: true, editType: 'ready-product', editKey: 'sku' },
    { key: 'other_expenses', label: 'Other order expenses', isCost: true, isRPOtherExp: true,
      tooltip: 'Packaging + Freight & Handling + Miscellaneous from the order, allocated per pcs' },
    { key: 'lost',           label: 'Lost',                isLost: true },
    { key: 'cogs',           label: 'COGS',                isCalculated: true },
  ],
  kits: [
    { key: 'product_name',    label: 'Kit Name',        isNameSimple: true },
    { key: 'sku',             label: 'SKU' },
    { key: 'asin',            label: 'ASIN' },
    { key: 'components_cost', label: 'Components Cost', isCost: true, isKitComponents: true },
    { key: 'lost',            label: 'Lost',            isLost: true },
    { key: 'cogs',            label: 'COGS',            isCalculated: true },
  ],
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'cogs_settings';

function loadSettings() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch { return {}; }
}

// ── Page component ───────────────────────────────────────────────────────────

function ProductCOGSPage() {
  const { tab } = useParams();
  const navigate = useNavigate();

  // Validate tab or fall back to first
  const activeTab = TABS.find(t => t.slug === tab) ? tab : TABS[0].slug;

  // Sync URL when activeTab derived value doesn't match param
  useEffect(() => {
    if (!tab || !TABS.find(t => t.slug === tab)) {
      navigate(`/product-cogs/${TABS[0].slug}`, { replace: true });
    }
  }, [tab, navigate]);

  // ── Persistent settings ──────────────────────────────────────────────────
  const saved = loadSettings();
  const [searchQuery,       setSearchQuery]       = useState(saved.searchQuery       || '');
  const [selectedBrands,    setSelectedBrands]    = useState(saved.selectedBrands    || []);
  const [selectedNiches,    setSelectedNiches]    = useState(saved.selectedNiches    || []);
  const [hourlyRate,        setHourlyRate]        = useState(saved.hourlyRate  ?? 15);
  const [toolsCost,         setToolsCost]         = useState(saved.toolsCost   ?? 0.25);
  const [lostPct,           setLostPct]           = useState(saved.lostPct     ?? 5);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        searchQuery, selectedBrands, selectedNiches, hourlyRate, toolsCost, lostPct,
      }));
    } catch {}
  }, [searchQuery, selectedBrands, selectedNiches, hourlyRate, toolsCost, lostPct]);

  // ── Business Expenses ─────────────────────────────────────────────────────
  const [bizExpData,      setBizExpData]      = useState(null);
  const [bizExpPanelOpen, setBizExpPanelOpen] = useState(false);

  // Fetch on mount with current month (panel will call onDataChange on every change)
  useEffect(() => {
    const now = new Date();
    api.get(`/api/cogs/business-expenses?year=${now.getFullYear()}&month=${now.getMonth() + 1}`)
      .then(r => { if (r.data) setBizExpData(r.data); })
      .catch(() => {});
  }, []);

  const bizExpPerUnit = bizExpData?.costPerUnit ?? 0;

  // ── Data ─────────────────────────────────────────────────────────────────
  const [data,   setData]   = useState([]);
  const [brands, setBrands] = useState([]);
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSaveCost = useCallback(async (type, id, value) => {
    // fetch не бросал исключение на HTTP-ошибках — глотаем их, чтобы reload ниже всегда выполнялся
    await api.put('/api/cogs/update-cost', { type, id, value })
      .catch(err => { if (!err.response) throw err; });
    // Reload data so COGS recalculates with the new cost
    setLoading(true);
    api.get(`/api/cogs/${activeTab}`)
      .then(r => r.data)
      .catch(() => [])
      .then(rows => { setData(Array.isArray(rows) ? rows : []); setLoading(false); });
  }, [activeTab]);

  // Fetch meta (brands + niches) once
  useEffect(() => {
    Promise.all([
      api.get('/plantoproduce/brands').then(r => r.data).catch(() => []),
      api.get('/plantoproduce/niches').then(r => r.data).catch(() => []),
    ]).then(([b, n]) => {
      setBrands(b);
      setNiches(n);
    });
  }, []);

  // Fetch COGS data for the active tab
  useEffect(() => {
    setLoading(true);
    setData([]);
    api.get(`/api/cogs/${activeTab}`)
      .then(r => r.data)
      .catch(() => [])
      .then(rows => {
        setData(Array.isArray(rows) ? rows : []);
        setLoading(false);
      });
  }, [activeTab]);

  // ── Filtering ─────────────────────────────────────────────────────────────
  const columns = COLUMNS[activeTab];

  const filteredData = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return data.filter(row => {
      const matchSearch = !q
        || (row.product_name?.toLowerCase().includes(q))
        || (row.sku?.toLowerCase().includes(q));
      const matchBrand = selectedBrands.length === 0
        || selectedBrands.includes(String(row.brandID));
      const matchNiche = selectedNiches.length === 0
        || selectedNiches.includes(String(row.niche_category_id));
      return matchSearch && matchBrand && matchNiche;
    });
  }, [data, searchQuery, selectedBrands, selectedNiches]);

  // ── Excel export ──────────────────────────────────────────────────────────
  const computeBreakdownCost = (items) =>
    (items ?? []).reduce((s, item) => {
      const sub = (item.bag_cost ?? 0) + (item.seeds ?? 0) + (item.labor_hours ?? 0) * hourlyRate;
      return s + sub * (Number(item.qty) || 1) * (1 + lostPct / 100);
    }, 0);

  const getSubtotalForExport = (row, cols) =>
    cols
      .filter(c => c.isCost && !c.isCalculated && !c.isLost)
      .reduce((sum, c) => {
        const raw = row[c.key] ?? 0;
        if (c.isFixedTools)      return sum + toolsCost;
        if (c.isLabor)           return sum + raw * hourlyRate;
        if (c.isSeedsOtherExp)   return sum + Number(row.seeds_other_expenses ?? 0);
        if (c.isFertOtherExp)    return sum + Number(row.fert_other_expenses ?? 0);
        if (c.isRPOtherExp)      return sum + Number(row.other_expenses ?? 0);
        if (c.isKitComponents)   return sum + Number(row.components_cost ?? 0);
        if (c.hasBreakdown)      return sum + computeBreakdownCost(row[c.hasBreakdown]);
        return sum + Number(raw);
      }, 0);

  const handleExportExcel = () => {
    const activeTabLabel = TABS.find(t => t.slug === activeTab)?.label || activeTab;

    const exportRows = filteredData.map(row => {
      const obj = {};
      const sub = getSubtotalForExport(row, columns);
      columns.forEach(col => {
        if (col.isCalculated) {
          obj[col.label] = sub + sub * lostPct / 100;
        } else if (col.isLost) {
          obj[col.label] = sub * lostPct / 100;
        } else if (col.isFixedTools) {
          obj[col.label] = toolsCost;
        } else if (col.isLabor) {
          obj[col.label] = (row[col.key] ?? 0) * hourlyRate;
        } else if (col.isKitComponents) {
          obj[col.label] = Number(row.components_cost ?? 0);
        } else if (col.hasBreakdown) {
          obj[col.label] = computeBreakdownCost(row[col.hasBreakdown]);
        } else {
          obj[col.label] = row[col.key] ?? '';
        }
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, activeTabLabel);
    XLSX.writeFile(wb, `COGS_${activeTabLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ── Tab navigation ────────────────────────────────────────────────────────
  const handleTabClick = (slug) => {
    navigate(`/product-cogs/${slug}`);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="cogs-root">
      <HeaderLogged />
      <div className="cogs-container">

        {/* Page header */}
        <div className="cogs-page-header">
          <div>
            <h2 className="cogs-title">Product COGS</h2>
            <p className="cogs-subtitle">Cost of Goods Sold breakdown by product</p>
          </div>
        </div>

        {/* Toolbar */}
        <COGSToolbar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          brands={brands}
          niches={niches}
          selectedBrands={selectedBrands}
          setSelectedBrands={setSelectedBrands}
          selectedNiches={selectedNiches}
          setSelectedNiches={setSelectedNiches}
          hourlyRate={hourlyRate}
          setHourlyRate={setHourlyRate}
          toolsCost={toolsCost}
          setToolsCost={setToolsCost}
          lostPct={lostPct}
          setLostPct={setLostPct}
          onExportExcel={handleExportExcel}
          onOpenBizExp={() => setBizExpPanelOpen(true)}
          bizExpData={bizExpData}
        />

        {/* Tabs */}
        <div className="cogs-tabs">
          {TABS.map(t => (
            <button
              key={t.slug}
              className={`cogs-tab${activeTab === t.slug ? ' active' : ''}`}
              onClick={() => handleTabClick(t.slug)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="cogs-loading">
            <i className="fas fa-spinner fa-spin"></i> Loading...
          </div>
        ) : (
          <COGSTable
            columns={columns}
            data={filteredData}
            hourlyRate={hourlyRate}
            toolsCost={toolsCost}
            lostPct={lostPct}
            onSaveCost={handleSaveCost}
            businessExpPerUnit={bizExpPerUnit}
          />
        )}
      </div>

      {/* Business Expenses popup */}
      {bizExpPanelOpen && (
        <BusinessExpensesPanel
          onClose={() => setBizExpPanelOpen(false)}
          onDataChange={setBizExpData}
        />
      )}
    </div>
  );
}

export default ProductCOGSPage;
